import React, { useState } from 'react';
import {
  GraduationCap, Factory, Landmark, Building2, TrendingUp, HandHeart, School, Users,
} from 'lucide-react';

const groups = [
  {
    key: 'education',
    icon: School,
    title: 'Education',
    body: 'K-12 leaders, superintendents, CTAE directors, and career-pathway educators shaping tomorrow’s workforce.',
  },
  {
    key: 'industry',
    icon: Factory,
    title: 'Industry',
    body: 'Employers in transportation, logistics, supply chain, and adjacent sectors seeking talent and long-term pipelines.',
  },
  {
    key: 'government',
    icon: Landmark,
    title: 'Government',
    body: 'Elected officials and public agency leaders aligning policy with workforce reality.',
  },
  {
    key: 'workforce-agencies',
    icon: Users,
    title: 'Workforce Agencies',
    body: 'WIOA boards, workforce partners, and public workforce systems investing in the region.',
  },
  {
    key: 'economic-development',
    icon: TrendingUp,
    title: 'Economic Development',
    body: 'Chambers, EDAs, and regional developers focused on jobs, growth, and mobility.',
  },
  {
    key: 'community-organizations',
    icon: HandHeart,
    title: 'Community Organizations',
    body: 'Nonprofits, faith communities, and CBOs connecting people to opportunity.',
  },
  {
    key: 'higher-education',
    icon: Building2,
    title: 'Higher Education',
    body: 'Technical colleges, HBCUs, and universities building the credentials and pipelines the workforce needs.',
  },
  {
    key: 'students',
    icon: GraduationCap,
    title: 'Students',
    body: 'The learners at the center — high school, college, adult learners exploring career pathways.',
  },
];

export const WhoShouldAttend = () => {
  const [active, setActive] = useState('industry');

  return (
    <section className="py-24 md:py-32 bg-aawta-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Who Should Attend</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              The leaders who can move the system — all in one room.
            </h2>
            <div className="divider-magenta mt-6" />
          </div>
          <p className="text-[17px] leading-relaxed text-aawta-muted lg:max-w-md">
            Change happens when every sector is at the table. Here’s who we’re convening —
            hover any card to explore.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {groups.map((g, i) => {
            const Icon = g.icon;
            const isActive = active === g.key;
            return (
              <button
                key={g.key}
                type="button"
                data-testid={`attendee-card-${g.key}`}
                onMouseEnter={() => setActive(g.key)}
                onFocus={() => setActive(g.key)}
                onClick={() => setActive(g.key)}
                style={{ animationDelay: `${i * 50}ms` }}
                className={`reveal group text-left rounded-2xl p-6 transition-all duration-300 border ${
                  isActive
                    ? 'bg-aawta-navy text-white border-aawta-navy shadow-aawta-elevated -translate-y-1'
                    : 'bg-white text-aawta-navy border-aawta-border hover:-translate-y-1 hover:shadow-aawta-elevated'
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                    isActive ? 'bg-aawta-magenta text-white' : 'bg-aawta-surface text-aawta-navy'
                  }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="mt-5 font-display font-semibold text-lg">{g.title}</h3>
                <p
                  className={`mt-2 text-sm leading-relaxed transition-colors duration-300 ${
                    isActive ? 'text-white/80' : 'text-aawta-muted'
                  }`}
                >
                  {g.body}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhoShouldAttend;
